// Where a row lands when it is put somewhere by hand: a timer dropped in
// after the current turn, or a combatant dragged up or down the tracker.
//
// The tracker has no order of its own — rows are sorted by their initiative
// score (ties by name), here and in Roll20. So "put this row here" has to be
// answered as a NUMBER, and that number is then what the Export writes onto
// Roll20's tracker. Roll20 accepts decimals in its turn field, which is what
// makes an in-between value possible at all.

/** The score a row should take to sit directly below `above` and directly
 *  above `below` (null = nothing below it: it goes last). Both are the
 *  scores of its new neighbours, already sorted high to low.
 *
 *  A whole number is preferred wherever one is free, because the tracker is
 *  read at a glance and "16" is easier to hold than "16.5"; decimals are
 *  spent only when the neighbours are already touching. */
export function insertScore(above: number, below: number | null): number {
  const top = Number.isFinite(above) ? above : 20;
  // Nothing below it: one clear step down, kept whole. A creature who later
  // rolls into that gap lands above it, which is right — the DM put this row
  // at the bottom of the order as it stood, not below everything for ever.
  if (below === null || !Number.isFinite(below)) return trim(Math.ceil(top) - 1);
  // A free whole number between them is the tidiest answer there is.
  const whole = Math.floor((top + below) / 2);
  if (whole > below && whole < top) return whole;
  const gap = top - below;
  // Tied (18 and 18): sit just under both — a list sorted by score cannot
  // express "between two rows that share one", and the DM would rather the
  // row land near than have the drop refused.
  if (gap <= 0) return trim(below - 0.01);
  // Touching (18 and 17): split the difference. Rounding is for readability
  // only, so it gives way when the neighbours are closer than it can tell
  // apart — a tidy number that lands ON a neighbour is worse than an untidy
  // one that lands between them.
  const mid = below + gap / 2;
  const tidy = trim(mid);
  return tidy > below && tidy < top ? tidy : mid;
}
/** Three decimals is as fine as the tracker ever needs to slice. */
const trim = (n: number) => Math.round(n * 1000) / 1000;

/** A score as the tracker shows it: whole numbers stay whole, an in-between
 *  value keeps just enough decimals to read (12.5, not 12.500000001). */
export const showScore = (n: number) => (Number.isInteger(n) ? String(n) : String(Math.round(n * 1000) / 1000));
